import React, { useState, useEffect, useCallback } from "react";
import "./../../../assets/css/purchase/form.css";

// React DateTime
import moment from "moment";

// Components
import Breadcrumbs from "../../components/Breadcrumbs";
import Sidebar from "../../components/Sidebar";
import ReportsTable from "../../components/Content/ReportsTable";
import DatePicker from "../../components/DatePicker";

// Utils
import { getAlert, getDataWithJoin } from "../../../utils/SamuiUtils";

const LoanCrateReport = () => {
  // ตัวแปรเก็บชื่อหน้าจอ
  const [name, setName] = useState(null);

  // ตัวแปรเก็บค่าเงื่อนไขการค้นหา
  const [searchCriteria, setSearchCriteria] = useState({});

  // ข้อมูลที่แสดงรายงาน
  const [filteredData, setFilteredData] = useState([]);

  // สำหรับ Table รายงาน
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(16);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const [exportController, setExportController] = useState({
    exportToExcel: null,
    isExporting: false,
    hasData: false,
  });

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    // เริ่มต้นหน้าจอ (เปิดครั้งแรก)
    setName("3. รายงานยืมลังประจำวัน");
    setSearchCriteria({
      fromDate: moment().format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
      fromDocNo: "",
      toDocNo: "",
      fromCustCode: "",
      toCustCode: "",
      returnStatus: null, // null = ทั้งหมด, 1 = คืนแล้ว, 0 = ยังไม่ได้คืน
    });
    setFilteredData([]);
  };

  const handleChangeDate = (value, name) => {
    setSearchCriteria((prev) => ({
      ...prev,
      [name]: value ? value.format("YYYY-MM-DD") : null,
    }));
  };

  const handleChangeCriteria = (e) => {
    const { name, value } = e.target;
    setSearchCriteria((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setSearchCriteria((prev) => ({
      ...prev,
      [name]: value === "" ? null : value,
    }));
  };

  const handleSearch = async () => {
    try {
      setFilteredData([]);

      // ส่วน SELECT
      const selectClause = `
        FORMAT(CAST(awh.WHDoc_Date AS DATETIME), 'dd/MM/yyyy') AS WHDoc_Date,
        awh.WHDoc_No,
        CASE awd.Item_Is_Return
          WHEN '1' THEN 'คืนแล้ว'
          WHEN '0' THEN 'ยังไม่ได้คืน'
          ELSE 'ยังไม่ได้คืน'
        END AS Return_Status,
        awh.WHDoc_Cust_Code,
        awh.WHDoc_Cust_Name,
        awd.Item_Code,
        awd.Item_Name,
        awd.Item_Qty,
        awd.Item_Unit
      `;

      // ส่วน FROM
      const tableClause = "API_1001_WHDOC_H awh";

      // ส่วน JOIN
      let joinClause =
        "LEFT JOIN API_1002_WHDOC_D awd ON awd.WHDoc_Id = awh.WHDoc_Id";

      // สร้าง WHERE clause
      let whereConditions = [];

      // Company ID
      const companyId = window.localStorage.getItem("company") || 1;
      whereConditions.push(`awh.Comp_Id = ${companyId}`);

      // ประเภทเอกสาร (ใบยืมลัง)
      whereConditions.push(`awh.WHDoc_Type = 6`);

      // เงื่อนไขช่วงวันที่
      if (searchCriteria.fromDate && searchCriteria.toDate) {
        whereConditions.push(
          `(CAST(awh.WHDoc_Date AS DATE) BETWEEN '${searchCriteria.fromDate}' AND '${searchCriteria.toDate}')`
        );
      } else if (searchCriteria.fromDate) {
        whereConditions.push(
          `(CAST(awh.WHDoc_Date AS DATE) >= '${searchCriteria.fromDate}')`
        );
      } else if (searchCriteria.toDate) {
        whereConditions.push(
          `(CAST(awh.WHDoc_Date AS DATE) <= '${searchCriteria.toDate}')`
        );
      }

      // เงื่อนไขช่วงเลขที่เอกสาร
      if (searchCriteria.fromDocNo && searchCriteria.toDocNo) {
        whereConditions.push(
          `(awh.WHDoc_No BETWEEN '${searchCriteria.fromDocNo}' AND '${searchCriteria.toDocNo}')`
        );
      } else if (searchCriteria.fromDocNo) {
        whereConditions.push(`awh.WHDoc_No >= '${searchCriteria.fromDocNo}'`);
      } else if (searchCriteria.toDocNo) {
        whereConditions.push(`awh.WHDoc_No <= '${searchCriteria.toDocNo}'`);
      }

      // เงื่อนไขช่วงรหัสลูกค้า
      if (searchCriteria.fromCustCode && searchCriteria.toCustCode) {
        whereConditions.push(
          `(awh.WHDoc_Cust_Code BETWEEN '${searchCriteria.fromCustCode}' AND '${searchCriteria.toCustCode}')`
        );
      } else if (searchCriteria.fromCustCode) {
        whereConditions.push(
          `awh.WHDoc_Cust_Code >= '${searchCriteria.fromCustCode}'`
        );
      } else if (searchCriteria.toCustCode) {
        whereConditions.push(
          `awh.WHDoc_Cust_Code <= '${searchCriteria.toCustCode}'`
        );
      }

      // เงื่อนไขสถานะการคืน
      if (
        searchCriteria.returnStatus !== null &&
        searchCriteria.returnStatus !== ""
      ) {
        whereConditions.push(
          `(awd.Item_Is_Return = '${searchCriteria.returnStatus}')`
        );
      }

      // รวม WHERE conditions และ ORDER BY
      const whereClause =
        whereConditions.length > 0
          ? `WHERE ${whereConditions.join(" AND ")}`
          : "";
      const orderClause = "ORDER BY awh.WHDoc_Date, awh.WHDoc_No ASC";

      // รวม JOIN กับ WHERE และ ORDER BY
      joinClause += ` ${whereClause} ${orderClause}`;

      console.log("SELECT:", selectClause);
      console.log("FROM:", tableClause);
      console.log("JOIN + WHERE + ORDER:", joinClause);

      const response = await getDataWithJoin(
        selectClause,
        tableClause,
        joinClause,
        "" // order ส่งเป็น empty string เพราะใส่ใน joinClause แล้ว
      );

      if (response && response.length > 0) {
        setFilteredData(response);
        getAlert("SUCCESS", `พบข้อมูล ${response.length} รายการ`);
      } else {
        setFilteredData([]);
        getAlert("INFO", "ไม่พบข้อมูลตามเงื่อนไขที่ค้นหา");
      }
    } catch (error) {
      console.error("Search Error:", error);
      setFilteredData([]);
      getAlert(
        "FAILED",
        error.response?.data?.message ||
          error.message ||
          "เกิดข้อผิดพลาดในการค้นหาข้อมูล"
      );
    }
  };

  const handleExportReady = useCallback((state) => {
    setExportController((prev) => {
      if (
        prev.exportToExcel === state.exportToExcel &&
        prev.isExporting === state.isExporting &&
        prev.hasData === state.hasData
      ) {
        return prev;
      }
      return state;
    });
  }, []);

  const handleExportClick = useCallback(() => {
    if (exportController?.exportToExcel) {
      exportController.exportToExcel();
    } else {
      getAlert("FAILED", "ไม่มีข้อมูลสำหรับ Export");
    }
  }, [exportController]);

  return (
    <>
      <Sidebar />
      <div className="main-panel">
        <div className="container">
          <div className="page-inner">
            <div className="page-header d-flex justify-content-between align-items-center">
              <Breadcrumbs
                page={name}
                items={[
                  { name: "รายงาน", url: "/reports" },
                  { name: name, url: "/loan-crate-report" },
                ]}
              />
            </div>
            <div className="row">
              {/* Criteria */}
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header">
                    <h4>เงื่อนไขการค้นหา</h4>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      {/* ช่วงวันที่ */}
                      <div className="col-md-2">
                        <label htmlFor="fromDate" className="form-label">
                          ช่วงวันที่ยืมสินค้า
                        </label>
                        <DatePicker
                          label=""
                          name="fromDate"
                          value={searchCriteria.fromDate}
                          onChange={(date) =>
                            handleChangeDate(date, "fromDate")
                          }
                          disabled={false}
                        />
                      </div>
                      <div className="col-md-2">
                        <label htmlFor="toDate" className="form-label">
                          ถึงวันที่
                        </label>
                        <DatePicker
                          label=""
                          name="toDate"
                          value={searchCriteria.toDate}
                          onChange={(date) => handleChangeDate(date, "toDate")}
                          disabled={false}
                        />
                      </div>

                      {/* ช่วงเลขที่เอกสาร */}
                      <div className="col-md-2">
                        <label htmlFor="fromDocNo" className="form-label">
                          เลขใบที่ยืมสินค้า
                        </label>
                        <input
                          type="text"
                          className="form-control input-spacing"
                          id="fromDocNo"
                          name="fromDocNo"
                          value={searchCriteria.fromDocNo || ""}
                          onChange={handleChangeCriteria}
                          placeholder="เลขที่เริ่มต้น"
                        />
                      </div>
                      <div className="col-md-2">
                        <label htmlFor="toDocNo" className="form-label">
                          ถึงใบเลขที่
                        </label>
                        <input
                          type="text"
                          className="form-control input-spacing"
                          id="toDocNo"
                          name="toDocNo"
                          value={searchCriteria.toDocNo || ""}
                          onChange={handleChangeCriteria}
                          placeholder="เลขที่สิ้นสุด"
                        />
                      </div>

                      {/* ช่วงรหัสลูกค้า */}
                      <div className="col-md-2">
                        <label htmlFor="fromCustCode" className="form-label">
                          รหัสลูกค้า
                        </label>
                        <input
                          type="text"
                          className="form-control input-spacing"
                          id="fromCustCode"
                          name="fromCustCode"
                          value={searchCriteria.fromCustCode || ""}
                          onChange={handleChangeCriteria}
                          placeholder="รหัสเริ่มต้น"
                        />
                      </div>
                      <div className="col-md-2">
                        <label htmlFor="toCustCode" className="form-label">
                          ถึงรหัสที่
                        </label>
                        <input
                          type="text"
                          className="form-control input-spacing"
                          id="toCustCode"
                          name="toCustCode"
                          value={searchCriteria.toCustCode || ""}
                          onChange={handleChangeCriteria}
                          placeholder="รหัสสิ้นสุด"
                        />
                      </div>

                      {/* สถานะการคืน */}
                      <div className="col-md-6">
                        <label className="form-label">สถานะการคืน</label>
                        <div className="mt-2">
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="returnStatus"
                              id="statusAll"
                              value=""
                              checked={searchCriteria.returnStatus === null}
                              onChange={handleRadioChange}
                            />
                            <label
                              className="form-check-label w-25"
                              htmlFor="statusAll"
                            >
                              ทั้งหมด
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="returnStatus"
                              id="statusReturned"
                              value="1"
                              checked={searchCriteria.returnStatus === "1"}
                              onChange={handleRadioChange}
                            />
                            <label
                              className="form-check-label w-25"
                              htmlFor="statusReturned"
                            >
                              คืนแล้ว
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="returnStatus"
                              id="statusNotReturned"
                              value="0"
                              checked={searchCriteria.returnStatus === "0"}
                              onChange={handleRadioChange}
                            />
                            <label
                              className="form-check-label w-25"
                              htmlFor="statusNotReturned"
                            >
                              ยังไม่ได้คืน
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6"></div>

                      <div className="col-md-12 d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white"
                          style={{
                            width: "10%",
                            marginRight: "20px",
                            backgroundColor: "#EF6C00",
                            fontSize: "16px",
                          }}
                          onClick={() => handleSearch()}
                        >
                          <i className="fas fa-search" /> ค้นหา
                        </button>
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white"
                          style={{
                            width: "10%",
                            marginRight: "20px",
                            backgroundColor: "#EF6C00",
                            fontSize: "16px",
                          }}
                          onClick={handleExportClick}
                          disabled={
                            !exportController.hasData || exportController.isExporting
                          }
                        >
                          <i className="fas fa-file-excel" />{" "}
                          {exportController.isExporting ? "กำลังส่งออก..." : "Export"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white"
                          style={{
                            width: "10%",
                            backgroundColor: "#EF6C00",
                            fontSize: "16px",
                          }}
                          onClick={() => initialize()}
                        >
                          <i className="fas fa-undo" /> เริ่มใหม่
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ReportsTable */}
              <ReportsTable
                currentItems={currentItems}
                currentPage={currentPage}
                handlePageChange={(pageNumber) => setCurrentPage(pageNumber)}
                dataMasterList={filteredData}
                itemsPerPage={itemsPerPage}
                setCurrentPage={setCurrentPage}
                setItemsPerPage={setItemsPerPage}
                fieldMappings={{
                  whDocDate: "WHDoc_Date",
                  whDocNo: "WHDoc_No",
                  returnStatus: "Return_Status",
                  tWhCode: "WHDoc_Cust_Code",
                  tWhName: "WHDoc_Cust_Name",
                  itemCode: "Item_Code",
                  itemName: "Item_Name",
                  itemQty: "Item_Qty",
                  itemUnit: "Item_Unit",
                }}
                showExportButton={false}
                onExportReady={handleExportReady}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoanCrateReport;
